/**
 * Data-only Rosie nav tree — shared by `useNavigationLinks` (adds icons) and
 * permissions / route-rule generation. Keep leaf `id` values stable: they are
 * permission resource keys (`${id}:read` / `${id}:write`).
 */
import {
  getRosieUserListPath,
  getManageRolesPath,
  getTxterListPath,
  getSclaAppListPath,
  getSealsLegacyAppListPath,
  getSealsAppListPath,
  getCreateMyTxtUserPath,
  getEmailTemplateListPath,
  getSclaAppStatsPath,
  getSealsAppStatsPath,
  getSclaInterestListPath,
  getSclaReaderAppListPath,
  getUploadSclaAppPdfPath,
  getUploadSclaInterviewSheetPath,
  getSclaCohortRosterPath,
  getSclaCohortFellowsPath,
  getSclaCohortTeamBuilderPath,
  getSclaCohortProgramPath,
  getSclaAttendancePath,
  getSclaChallengeCompletionPath,
  getHackathonStudentListPath,
  getHackathonVolunteerListPath,
  getAutomatedCallsCallLogsPath,
  getAutomatedCallsScriptManagementPath,
  getAutomatedCallsBatchCallsPath,
  getBitwardenProjectsPath,
  getDropboxAuditPath,
  getArcadeGamesPath,
  getBatchSmsCampaignsPath,
  getBatchSmsInboxPath,
  getBatchSmsMessagesPath,
  getBatchSmsConversationsPath,
  getCreateBatchSmsCampaignPath,
  getSurveyCreatePath,
  getSurveysListPath,
  getAiWorkshopSurveyResultsPath,
  getTxtMerchProductsPath,
  getTxtMerchMaterialsPath,
  getTxtMerchSalesPath,
  // getTxtMerchExpensesPath,
  getTxtProgramsPath,
  getTxtProgramRegistrationsPath,
  getEquipmentListPath,
  getApprenticeAppListPath,
  getBusinessCardAppListPath,
  getBusinessCardAddPath,
  getSignedFormsTemplatesPath,
  getSignedFormsAssignmentsPath,
  getBagelConfigPath,
  getStudentActivitiesPageListPath,
  getStudentActivitiesAssignmentsListPath,
  getLabelPrintingPath,
  getSclaMobileApplicationsPath,
} from 'utils/navigation';

export type NavTreeDataNode = {
  id: string;
  title: string;
  subtitle?: string;
  sectionTitle?: string;
  /** Display order within the parent group. Does not affect permission strings. */
  order?: number;
  to?: string;
  children?: NavTreeDataNode[];
};

export function buildBaseNavTreeData(year: string): NavTreeDataNode[] {
  const programsChildren: NavTreeDataNode[] = [
    {
      id: 'programs-scla',
      sectionTitle: 'Programs',
      title: 'SCLA',
      subtitle: 'Summer Coding Leadership Academy',
      children: [
        {
          id: 'programs-scla-recruitment',
          title: 'Recruitment',
          subtitle: 'Applications and enrollment',
          children: [
            {
              id: 'programs-scla-recruitment-stats',
              title: 'Stats',
              to: getSclaAppStatsPath(year),
            },
            {
              id: 'programs-scla-recruitment-apps',
              title: 'Applications',
              to: getSclaAppListPath(
                1,
                30,
                'createdOn',
                'desc',
                'all',
                year,
                'all',
              ),
            },
            {
              id: 'programs-scla-recruitment-interests',
              title: 'School Presentation Leads',
              to: getSclaInterestListPath(
                1,
                30,
                'createdOn',
                'desc',
                year,
                'all',
              ),
            },
            {
              id: 'programs-scla-recruitment-readers',
              title: 'SCLA Readers',
              to: getSclaReaderAppListPath(
                1,
                30,
                'createdOn',
                'desc',
                'all',
                year,
                'all',
              ),
            },
            {
              id: 'programs-scla-recruitment-interview-sheet',
              title: 'Interview roster import',
              to: getUploadSclaInterviewSheetPath(),
            },
            {
              id: 'programs-scla-recruitment-upload',
              title: 'Upload Paper Application',
              to: getUploadSclaAppPdfPath(),
            },
          ],
        },
        {
          id: 'programs-scla-cohorts',
          title: 'Cohorts',
          subtitle: 'Groups and schedules',
          children: [
            {
              id: 'programs-scla-cohorts-roster',
              title: 'Cohort Roster',
              to: getSclaCohortRosterPath(year),
            },
            {
              id: 'programs-scla-cohorts-fellows',
              title: 'Fellows',
              to: getSclaCohortFellowsPath(year),
            },
            {
              id: 'programs-scla-cohorts-teams',
              title: 'Teams',
              to: getSclaCohortTeamBuilderPath(year),
            },
            {
              id: 'programs-scla-cohorts-program',
              title: 'Cohort Program',
              to: getSclaCohortProgramPath(year),
            },
            {
              id: 'programs-scla-cohorts-attendance',
              title: 'Attendance',
              to: getSclaAttendancePath(year),
            },
            {
              id: 'programs-scla-cohorts-challenge-completion',
              title: 'Challenge Completion',
              to: getSclaChallengeCompletionPath(year),
            },
          ],
        },
        {
          id: 'programs-scla-mobile-applications',
          title: 'Mobile Applications',
          subtitle: "Student capstone apps' users",
          to: getSclaMobileApplicationsPath(),
        },
      ],
    },
    {
      id: 'programs-hnc-hackathon',
      title: 'HNC',
      children: [
        {
          id: 'programs-hnc-hackathon-youth-information',
          order: 0,
          title: 'Youth Information',
          to: getHackathonStudentListPath(),
        },
        {
          id: 'programs-hnc-hackathon-volunteer-information',
          order: 1,
          title: 'Volunteer Information',
          to: getHackathonVolunteerListPath(),
        },
        {
          id: 'programs-hnc-hackathon-student-stats',
          title: 'Student Stats',
        },
        {
          id: 'programs-hnc-hackathon-volunteer-stats',
          title: 'Volunteer Stats',
        },
      ],
    },
    {
      id: 'programs-seals',
      title: 'SEALS',
      children: [
        {
          id: 'programs-seals-applications',
          order: 0,
          title: 'Applications',
          to: getSealsAppListPath(),
        },
        {
          id: 'programs-seals-legacy-applications',
          order: 1,
          title: 'Legacy Applications',
          to: getSealsLegacyAppListPath(),
        },
        {
          id: 'programs-seals-legacy-stats',
          order: 2,
          title: 'Legacy Stats',
          to: getSealsAppStatsPath(),
        },
      ],
    },
  ];

  const toolsSmsNode: NavTreeDataNode = {
    id: 'tools-batch-sms',
    title: 'SMS',
    children: [
      {
        id: 'tools-batch-sms-campaigns',
        order: 0,
        title: 'Campaigns',
        to: getBatchSmsCampaignsPath(),
      },
      {
        id: 'tools-batch-sms-create-campaign',
        order: 1,
        title: 'Create Campaign',
        to: getCreateBatchSmsCampaignPath(),
      },
      {
        id: 'tools-batch-sms-inbox',
        order: 2,
        title: 'Inbox',
        to: getBatchSmsInboxPath(),
      },
      {
        id: 'tools-batch-sms-messages',
        order: 3,
        title: 'All Messages',
        to: getBatchSmsMessagesPath(),
      },
      {
        id: 'tools-batch-sms-conversations',
        order: 4,
        title: 'Conversations',
        to: getBatchSmsConversationsPath(),
      },
    ],
  };

  const toolsSurveysNode: NavTreeDataNode = {
    id: 'tools-surveys',
    title: 'Surveys',
    children: [
      {
        id: 'tools-surveys-list',
        order: 0,
        title: 'All Surveys',
        to: getSurveysListPath(),
      },
      {
        id: 'tools-surveys-create',
        order: 1,
        title: 'Create Survey',
        to: getSurveyCreatePath(),
      },
      {
        id: 'tools-surveys-ai-workshop-results',
        order: 2,
        title: 'AI Workshop Results',
        to: getAiWorkshopSurveyResultsPath(),
      },
    ],
  };

  const toolsCallsNode: NavTreeDataNode = {
    id: 'tools-automated-calls',
    title: 'Calls',
    children: [
      {
        id: 'tools-automated-calls-call-logs',
        order: 0,
        title: 'Call Logs',
        to: getAutomatedCallsCallLogsPath(),
      },
      {
        id: 'tools-automated-calls-script-management',
        order: 1,
        title: 'Script Management',
        to: getAutomatedCallsScriptManagementPath(),
      },
      {
        id: 'tools-automated-calls-batch-calls',
        order: 2,
        title: 'Batch Calls',
        to: getAutomatedCallsBatchCallsPath(),
      },
      {
        id: 'tools-automated-calls-batch-create',
        title: 'Automated Calls Batch Create',
      },
    ],
  };

  const businessLabsChildren: NavTreeDataNode[] = [
    {
      id: 'labs-apprentice',
      title: 'Apprentice',
      to: getApprenticeAppListPath(),
    },
    {
      id: 'labs-business',
      title: 'Business Contacts',
      to: getBusinessCardAppListPath(),
    },
    {
      id: 'labs-business-cards',
      title: 'Add Business Card',
      to: getBusinessCardAddPath(),
    },
  ];

  const toolsTxtFormsChildren: NavTreeDataNode[] = [
    {
      id: 'other-txt-forms-templates',
      title: 'Templates',
      to: getSignedFormsTemplatesPath(),
    },
    {
      id: 'other-txt-forms-recipients',
      title: 'Recipients',
      to: getSignedFormsAssignmentsPath(),
    },
  ];

  const businessTxtProgramsChildren: NavTreeDataNode[] = [
    {
      id: 'other-txt-programs-list',
      title: 'Programs',
      to: getTxtProgramsPath(),
    },
    {
      id: 'other-txt-programs-enrollments',
      title: 'Registrations',
      to: getTxtProgramRegistrationsPath(),
    },
  ];

  const businessTxtMerchChildren: NavTreeDataNode[] = [
    {
      id: 'other-txt-merch-products',
      title: 'Products',
      to: getTxtMerchProductsPath(),
    },
    {
      id: 'other-txt-merch-materials',
      title: 'Materials',
      to: getTxtMerchMaterialsPath(),
    },
    {
      id: 'other-txt-merch-sales',
      title: 'Product sales',
      to: getTxtMerchSalesPath(),
    },
  ];

  const toolsTxtLearningChildren: NavTreeDataNode[] = [
    {
      id: 'txt-learning-nav-pages',
      title: 'Learning Pages',
      to: getStudentActivitiesPageListPath(),
    },
    {
      id: 'tools-txt-learning-assignments',
      title: 'Assignments',
      to: getStudentActivitiesAssignmentsListPath(),
    },
  ];

  return [
    {
      id: 'profiles-txters-nav',
      title: 'My TXT Data',
      subtitle: 'Browse students, staff, and records',
      sectionTitle: 'Profiles',
      children: [
        {
          id: 'profiles-txters',
          title: 'Profile List',
          to: getTxterListPath(),
        },
        {
          id: 'profiles-my-txt-create-user',
          title: 'Create Profile',
          to: getCreateMyTxtUserPath(),
        },
        {
          id: 'txters-details',
          title: 'Details',
        },
      ],
    },
    ...programsChildren,
    {
      id: 'business-labs',
      title: 'Labs',
      sectionTitle: 'Business',
      children: businessLabsChildren,
    },
    {
      id: 'other-txt-programs',
      title: 'TXT Programs',
      children: businessTxtProgramsChildren,
    },
    {
      id: 'other-txt-merch',
      title: 'TXT Merch',
      children: businessTxtMerchChildren,
    },
    {
      ...toolsSmsNode,
      sectionTitle: 'Tools',
    },
    toolsCallsNode,
    toolsSurveysNode,
    {
      id: 'tools-label-printing',
      title: 'Label Printing',
      subtitle: 'Print custom labels',
      to: getLabelPrintingPath(),
    },
    {
      id: 'other-txt-forms',
      title: 'TXT Forms',
      children: toolsTxtFormsChildren,
    },
    {
      id: 'tools-txt-learning',
      title: 'TXT Learning',
      children: toolsTxtLearningChildren,
    },
    {
      id: 'other-equipment-inventory',
      title: 'Equipment Inventory',
      subtitle: 'Track equipment',
      to: getEquipmentListPath(),
    },
  ];
}

export function buildFullNavTreeData(year: string): NavTreeDataNode[] {
  return [
    ...buildBaseNavTreeData(year),
    {
      id: 'admin-tools',
      title: 'Admin tools',
      sectionTitle: 'Admin',
      children: [
        {
          id: 'admin-tools-email-templates',
          order: 0,
          title: 'Email Templates',
          to: getEmailTemplateListPath(),
        },
        {
          id: 'admin-tools-rosie-user-management',
          order: 1,
          title: 'Rosie User Management',
          to: getRosieUserListPath(),
        },
        {
          id: 'admin-tools-manage-roles',
          order: 2,
          title: 'Manage Roles',
          to: getManageRolesPath(),
        },
        {
          id: 'admin-tools-bitwarden-secrets',
          order: 3,
          title: 'Bitwarden Secrets',
          to: getBitwardenProjectsPath(),
        },
        {
          id: 'admin-tools-dropbox-audit',
          order: 4,
          title: 'Dropbox Audit',
          to: getDropboxAuditPath(),
        },
        {
          // Not "admin-tools-*" — matches the backend's own
          // `arcade-games:read`/`arcade-games:write` permission keys 1:1 so
          // the client-side gate agrees with what the API actually enforces.
          id: 'arcade-games',
          order: 5,
          title: 'Arcade Games',
          subtitle: 'Student game publishing + RetroPie cabinets',
          to: getArcadeGamesPath(),
        },
        {
          id: 'bagel-config',
          title: 'Bagel AI Settings',
          subtitle: 'Documents, prompts, and knowledge base',
          to: getBagelConfigPath(),
        },
        {
          id: 'admin-tools-emails-template-create',
          title: 'Email Template Create',
        },
        {
          id: 'admin-tools-emails-template-detail',
          title: 'Email Template Detail',
        },
        {
          id: 'admin-tools-rosie-user-detail',
          title: 'Rosie User Detail',
        },
        {
          id: 'admin-tools-rosie-user-new',
          title: 'Rosie User New',
        },
        {
          id: 'admin-tools-activity-logs',
          title: 'Activity Logs',
        },
      ],
    },
  ];
}
